/**
 * Was der Nutzer an der gewählten Anlage darf (HM-160, ADR 0043).
 *
 * Eine eigene Anlage darf er ganz; eine geteilte nach der Stufe ihrer
 * Teilung. Der Rahmen kennt beides und stellt die Antwort als Kontext bereit,
 * die Ansichten fragen sie ab: Was die Stufe nicht erlaubt, wird **ausgeblendet,
 * nicht ausgegraut** (Regel 10) — und das ist **kein Schutz**, den hält der
 * Server.
 *
 * Die Regeln stehen hier und nicht in jeder Ansicht: „schreiben darf alles
 * außer löschen und teilen" an zwanzig Stellen hinzuschreiben hieße, dass eine
 * davon es irgendwann anders meint.
 */
import { createContext, useContext } from "react";
import type { ApiComponents } from "@hausmeter/sdk";

type SharedSite = ApiComponents["schemas"]["SharedSite"];
type InvitationStatus = ApiComponents["schemas"]["SiteShareInvitation"]["status"];

export type SiteAccess = "owner" | "write" | "read";

export interface SiteAccessRights {
  readonly access: SiteAccess;
  /** Ändern: Stammdaten, Zähler, Messkonzept, Geräte, Wallbox. */
  readonly canWrite: boolean;
  /** Löschen, archivieren, freigeben — nur der Besitzer. */
  readonly canDelete: boolean;
  /** Teilen und Bewohner einladen — nur der Besitzer. */
  readonly canManage: boolean;
}

export function rightsOf(access: SiteAccess): SiteAccessRights {
  return {
    access,
    canWrite: access !== "read",
    canDelete: access === "owner",
    canManage: access === "owner",
  };
}

/** Die Stufe an dieser Anlage — `owner`, wenn sie nicht unter den geteilten steht. */
export function siteAccessOf(siteId: string | null, shared: readonly SharedSite[]): SiteAccess {
  if (siteId === null) return "owner";
  return shared.find((entry) => entry.site.id === siteId)?.access ?? "owner";
}

/** „Geteilt von Erika Muster · Lesen" — die zweite Zeile im Umschalter. */
export function sharedSiteLabel(entry: SharedSite): string {
  const name = [entry.owner.firstName, entry.owner.lastName].filter((part) => part !== null && part !== "").join(" ");
  return `Geteilt von ${name.length > 0 ? name : entry.owner.email} · ${shareAccessLabel(entry.access)}`;
}

export function shareAccessLabel(access: "read" | "write"): string {
  return access === "write" ? "Lesen und Schreiben" : "Lesen";
}

/**
 * Eine Einladung, die nicht mehr angenommen werden kann, in der Liste des
 * Einladenden (#47, #53): Marke und Hinweis, oder `null`, solange sie offen ist.
 * „Zurücknehmen" gibt es dann nicht mehr — die Antwort ist eine neue Einladung.
 * Ob sie abgelaufen ist, sagt die API (`expired`), nicht die Uhr des Browsers.
 */
export function endedInvitation(status: InvitationStatus): { readonly badge: string; readonly hint: string } | null {
  switch (status) {
    case "pending":
      return null;
    case "declined":
      return { badge: "abgelehnt", hint: "abgelehnt — du kannst neu einladen" };
    case "expired":
      return { badge: "abgelaufen", hint: "abgelaufen — neu einladen, damit der Zugang entsteht" };
    // Angenommene und zurückgenommene liefert die Liste nicht; stünden sie da, ist „Zurücknehmen" falsch.
    case "accepted":
      return { badge: "angenommen", hint: "angenommen" };
    case "revoked":
      return { badge: "zurückgenommen", hint: "zurückgenommen" };
  }
}

export const SiteAccessContext = createContext<SiteAccess>("owner");

export function useSiteAccess(): SiteAccessRights {
  return rightsOf(useContext(SiteAccessContext));
}
